/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import Component from 'can/component/component';
import appContext from 'scripts/app-context';
import Swiper from 'swiper';

import template from './probability.stache';
import {
    WIN_PROBABILITY,
    MAKE_CUT_PROBABILITY,
    TOP_10_PROBABILITY,
    getFieldName,
} from './types';
import {
    sendTabChangeAnalytics,
    sendChartClickAnalytics,
    sendVsToggleAnalytics,
    sendProbabilityChartArrowClickAnalytics,
    getPlayerName,
    sendChartAnalytics,
    sendLatestProbabilityChartAnalytics,
} from './controllers/analytics.controller';
import { prepareChartData, isChartDataEqual } from './computes';
import { PROBABILITY_PAGE_TYPE } from 'components/probability/tooltip-handler';
import dtm from 'scripts/services/dtm';
import { sendFullRecapProbabilityClickAnalitycs } from 'components/recap/controllers/analitycs.controller';

export const getRoundsData = (probability, round) => {
    if (!round) {
        return probability.serialize();
    }

    if (round === 0) {
        return probability.serialize();
    }

    return _.reduce(
        probability,
        (memo, roundData) => {
            if (Number(round) === roundData.roundNumber) {
                return [roundData.serialize()];
            }

            return memo;
        },
        [],
    );
};

export const dispatchDraggingOn = () =>
    appContext.model.dispatch('probability-swiper-dragging', [true]);

export const dispatchDraggingOff = () =>
    appContext.model.dispatch('probability-swiper-dragging', [false]);

Component.extend({
    tag: 'player-probability',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            showHeader: {
                type: 'boolean',
                value: false,
            },
            hideTooltip: {
                type: 'boolean',
            },
            showTooltip: {
                get() {
                    return !this.attr('hideTooltip');
                },
            },
            showVideo: {
                type: 'boolean',
                value: false,
            },
            player: {
                type: 'object',
            },
            round: {
                type: 'number',
            },
            showLeaders: {
                type: 'boolean',
                value: true,
            },
            showLeadersToggle: {
                type: 'boolean',
                value: false,
            },
            probability: {
                type: 'array',
                value: [],
            },
            winProbabilityLabel: {
                type: 'string',
                value: WIN_PROBABILITY,
            },
            top10ProbabilityLabel: {
                type: 'string',
                value: TOP_10_PROBABILITY,
            },
            makeCutProbabilityLabel: {
                type: 'string',
                value: MAKE_CUT_PROBABILITY,
            },
            probabilityField: {
                type: 'string',
            },
            probabilityType: {
                type: 'string',
            },
            probabilities: {
                type: 'object',
                value: {},
            },
            isSwiperBeginning: {
                type: 'boolean',
                value: true,
            },
            isSwiperEnd: {
                type: 'boolean',
                value: false,
            },
            probabilityLabels: {
                type: 'array',
                value: [],
            },
            defaultProbabilityType: {
                get() {
                    return this.probabilities.dayNumber > 2
                        ? WIN_PROBABILITY
                        : MAKE_CUT_PROBABILITY;
                },
            },
            isNavOpen: {
                type: 'boolean',
                value: false,
            },
            chartsData: {
                type: 'array',
                value: [],
            },
            yAxis: {
                type: 'array',
                value: [],
            },
            pageType: {
                type: 'string',
                value: PROBABILITY_PAGE_TYPE.LEADERBOARD,
            },
            isExpanded: {
                type: 'boolean',
            },
            showDropdown: {
                type: 'boolean',
            },
        },
        toggleNavOpen() {
            this.attr('isNavOpen', !this.attr('isNavOpen'));
        },
        changeProbabilityType(probabilityType) {
            this.attr('isNavOpen', false);
            this.dispatch('probabilityChange', [probabilityType]);
            dtm.trackWinProbabilityTrackerTabClick(probabilityType);
        },
        debouncedSendSlide: _.debounce((viewModel) => {
            const [firstName, lastName] = viewModel.attr('player.title')
                ? viewModel.attr('player.title').split(' ')
                : [];

            const player = viewModel.attr('player.firstName')
                ? viewModel.attr('player')
                : {
                      firstName,
                      lastName,
                  };

            dtm.trackProbabilityChartSlide(
                getPlayerName(player),
                viewModel.attr('probabilityType'),
                viewModel.attr('pageType'),
            );
        }, 100),
        trackRecap(ctx, el, ev) {
            if (ev.ctrlKey) {
                return;
            }

            ev.preventDefault();
            sendFullRecapProbabilityClickAnalitycs(
                ctx.attr('probabilityType'),
                ctx.attr('player'),
                ctx.attr('pageType'),
            );

            window.open(el.attr('href'), '_self');
        },
    },
    events: {
        inserted() {
            this.initSwiper();
            this.registerModelListeners();

            this.viewModel.attr(
                'probabilityField',
                getFieldName(this.viewModel.attr('probabilityType')),
            );
            this.updateProbability();
            this.updateProbabilityTypes();

            this.viewModel.attr('tooltip', this.element.find('.c-tooltip'));
        },

        registerModelListeners() {
            this.viewModel.bind('probabilityType', (_, value) => {
                this.viewModel.attr('probabilityField', getFieldName(value));
                this.updateProbability();

                const isOnLive =
                    this.viewModel.attr('pageType') ===
                    PROBABILITY_PAGE_TYPE.LIVE_VIDEO;
                const player = this.viewModel.attr('player');

                sendTabChangeAnalytics(
                    value,
                    isOnLive ? player.attr('title') : getPlayerName(player),
                    this.viewModel.attr('pageType'),
                );
            });

            this.viewModel.bind('probabilities', () => {
                this.updateProbability();
            });

            this.viewModel.bind('round', () => {
                this.updateProbability();
                this.updateProbabilityTypes();
            });
        },

        initSwiper() {
            this.swiper = new Swiper(this.element.find('.js-chart-container'), {
                slidesPerView: 'auto',
                freeMode: true,
                observer: true,
                scrollbar: {
                    el: this.element.find('.swiper-scrollbar'),
                    dragSize: 28,
                    draggable: true,
                    snapOnRelease: false,
                },
                on: {
                    observerUpdate: () => this.updateSwiperSize(),
                    touchStart: dispatchDraggingOn,
                    touchEnd: dispatchDraggingOff,
                    scrollbarDragStart: dispatchDraggingOn,
                    scrollbarDragEnd: dispatchDraggingOff,
                },
            });

            this.swiper.on('progress', (progress) =>
                this.handleSwiperProgress(progress),
            );
        },

        handleSwiperProgress(progress) {
            if (!this.swiper) {
                return;
            }

            if (progress) {
                this.viewModel.debouncedSendSlide(this.viewModel);
            }

            if (this.swiper.size === this.swiper.virtualSize) {
                this.viewModel.attr('isSwiperEnd', true);
                this.viewModel.attr('isSwiperBeginning', true);
                return;
            }

            this.viewModel.attr('isSwiperEnd', progress >= 1);
            this.viewModel.attr('isSwiperBeginning', progress <= 0);
        },

        updateSwiperSize() {
            if (!this.swiper || !this.swiper.scrollbar) {
                return;
            }

            this.swiper.update();
            this.swiper.scrollbar.updateSize();
        },

        updateProbability() {
            const probability = _.get(
                this.viewModel.attr('probabilities'),
                this.viewModel.attr('probabilityField'),
            );

            if (!probability) {
                return;
            }

            const chartsData = this.viewModel.attr('chartsData').serialize();
            const probabilityType = this.viewModel.attr('probabilityType');
            const round = this.viewModel.attr('round');
            const data = getRoundsData(probability, round);
            const preparedData = prepareChartData(data);

            if (!isChartDataEqual(preparedData, chartsData)) {
                this.viewModel.attr('chartsData', preparedData);
                this.viewModel.attr(
                    'yAxis',
                    preparedData[0] ? preparedData[0].yAxis : [],
                );
                this.viewModel.attr('probability', probability);
            }

            this.element.find('.js-graph-type-label').removeClass('is-active');
            this.element
                .find(`.js-graph-type-label[data-type="${probabilityType}"]`)
                .addClass('is-active');
        },

        checkRoundNotExist() {
            const round = this.viewModel.attr('round');

            if (round === 0) {
                return;
            }

            if (_.has(this.viewModel.attr('probability'), round - 1)) {
                return;
            }

            this.viewModel.attr(
                'probabilityType',
                this.viewModel.attr('defaultProbabilityType'),
            );
        },

        updateProbabilityTypes() {
            const round = this.viewModel.attr('round');
            const probabilityType = this.viewModel.attr('probabilityType');
            const baseTypes = [
                WIN_PROBABILITY,
                TOP_10_PROBABILITY,
                MAKE_CUT_PROBABILITY,
            ];
            const mapTypes = (type) => ({
                type,
                active: probabilityType === type,
            });

            if (round === 0) {
                this.viewModel.attr(
                    'probabilityTypes',
                    baseTypes.map(mapTypes),
                );
                return;
            }

            const types = baseTypes
                .filter((type) => type !== MAKE_CUT_PROBABILITY || round <= 2)
                .map(mapTypes);

            this.viewModel.attr('probabilityTypes', types);
        },

        updateLeadersToggle() {
            const showLeadersToggle =
                this.viewModel.attr('probabilities').dayNumber > 1 &&
                this.viewModel.probabilityType === WIN_PROBABILITY &&
                this.viewModel.probabilityLabels
                    .serialize()
                    .findIndex(({ label }) => label === WIN_PROBABILITY) >= 0;

            this.viewModel.attr('showLeadersToggle', showLeadersToggle);
            this.viewModel.attr(
                'showLeaders',
                this.viewModel.attr('showLeadersToggle'),
            );
        },

        '.c-player-probability__chart mouseup'() {
            sendChartClickAnalytics(
                this.viewModel.attr('probabilityType'),
                this.viewModel.attr('player'),
                this.viewModel.attr('pageType'),
            );
        },

        '.js-leaders-toggle change'() {
            const newToggleState = !this.viewModel.attr('showLeaders');

            if (newToggleState) {
                sendVsToggleAnalytics(this.viewModel.attr('player'));
            }

            this.viewModel.attr('showLeaders', newToggleState);
        },

        '.js-dropdown-arrow-right mouseup'(el, event) {
            event.stopPropagation();
            sendProbabilityChartArrowClickAnalytics(
                this.viewModel.attr('probabilityType'),
                this.viewModel.attr('player'),
            );
        },

        '.js-dropdown-arrow-left mouseup'(el, event) {
            event.stopPropagation();
            sendProbabilityChartArrowClickAnalytics(
                this.viewModel.attr('probabilityType'),
                this.viewModel.attr('player'),
            );
        },
        '.s-graph-dot mouseover'() {
            if (window.globalSettings.pageType === 'latest') {
                sendLatestProbabilityChartAnalytics({
                    type: this.viewModel.attr('probabilityType'),
                    player: this.viewModel.attr('player.player'),
                });
            } else {
                const value = this.viewModel.attr('activeTooltipId');

                sendChartAnalytics(
                    {
                        type: this.viewModel.attr('probabilityType'),
                        player: this.viewModel.attr('player.player'),
                        pageType: this.viewModel.attr('pageType'),
                    },
                    value,
                );
            }
        },
    },
});
